import {
  FALLBACK_PRICING_CONFIG,
  FALLBACK_SERVICE_PRICING,
  normalizePricingConfig,
  type PricingConfig,
} from '@/lib/admin/pricing-config';

const ROOM_CHARGE = 5.5;
const BATHROOM_CHARGE = 5;
const VAT_MULTIPLIER = 1.2;
const LEGACY_CONSTRUCTION_PRICE_PER_SQM = 3.2;
const LEGACY_CONSTRUCTION_STANDARD_MULT = 4.6 / 3.2;
const LEGACY_CONSTRUCTION_HEAVY_MULT = 6.8 / 3.2;

function replaceLegacyRate(value: number | undefined, legacy: number, next: number): number {
  if (
    value === undefined ||
    value === null ||
    Number.isNaN(value) ||
    Math.abs(value - legacy) < 1e-6
  ) {
    return next;
  }
  return value;
}

const MARKET_FLOORS = {
  residentialTiers: {
    tier_1: 36,
    tier_2: 47.45,
    tier_3: 54,
    tier_4: 60.55,
    tier_5: 76.91,
    tier_6: 83.95,
    tier_7: 106.04,
    tier_8: 128.13,
  },
  officePricePerSqm: 0.6895,
  officeBathroomPrice: 4.2,
  moveInPricePerSqm: 3.2,
  moveOutPricePerSqm: 3.0,
  moveBothPricePerSqm: 2.8,
  constructionPricePerSqm: 2,
  constructionWindowPrice: 6,
  windowPricePerWindow: 6,
  stairwellBase: 15,
  stairwellPerFloor: 4,
  stairwellHandrailsPerFloor: 2,
  stairwellMailboxes: 1.5,
  stairwellDoors: 3,
  stairwellBasement: 10,
} as const;

const RESIDENTIAL_TIER_INPUTS = [
  {
    key: 'tier_1',
    name: 'Tier 1',
    sqm: '25-40 m²',
    rooms: '1 room',
    baths: '1 bath',
    minRooms: 1,
    minBaths: 1,
  },
  {
    key: 'tier_2',
    name: 'Tier 2',
    sqm: '45-60 m²',
    rooms: '2 rooms',
    baths: '1 bath',
    minRooms: 1,
    minBaths: 1,
  },
  {
    key: 'tier_3',
    name: 'Tier 3',
    sqm: '65-80 m²',
    rooms: '3 rooms',
    baths: '1 bath',
    minRooms: 1,
    minBaths: 1,
  },
  {
    key: 'tier_4',
    name: 'Tier 4',
    sqm: '85-100 m²',
    rooms: '3 rooms',
    baths: '2 baths',
    minRooms: 1,
    minBaths: 1,
  },
  {
    key: 'tier_5',
    name: 'Tier 5',
    sqm: '105-150 m²',
    rooms: '4 rooms',
    baths: '2 baths',
    minRooms: 1,
    minBaths: 1,
  },
  {
    key: 'tier_6',
    name: 'Tier 6',
    sqm: '150-200 m²',
    rooms: '5 rooms',
    baths: '2 baths',
    minRooms: 1,
    minBaths: 1,
  },
  {
    key: 'tier_7',
    name: 'Tier 7',
    sqm: '201-275 m²',
    rooms: '6 rooms',
    baths: '3 baths',
    minRooms: 1,
    minBaths: 1,
  },
  {
    key: 'tier_8',
    name: 'Tier 8',
    sqm: '276-350 m²',
    rooms: '7 rooms',
    baths: '4 baths',
    minRooms: 1,
    minBaths: 1,
  },
] as const;

export interface PublicPricingSnapshot {
  lowestBookablePrice: number;
  /** Admin-configured add-on prices, keyed by add-on id. */
  addOnsPricing: Record<string, number>;
  residentialPricing: {
    tiers: Array<{
      name: string;
      sqm: string;
      regular: number;
      expert: number;
      rooms: string;
      baths: string;
    }>;
    extras: Array<{ name: string; price: number }>;
    frequencyDiscounts: Array<{
      frequency: string;
      discount: number;
      description: string;
    }>;
  };
  moveInOutPricing: {
    services: Array<{
      name: string;
      pricePerSqm: number;
      description: string;
      discount?: number;
    }>;
    examples: Array<{
      sqm: number;
      moveIn: number;
      moveOut: number;
      both: number;
    }>;
  };
  officePricing: {
    pricePerSqm: number;
    bathroomPrice: number;
    tiers: Array<{
      name: string;
      sqm: string;
      monthly: string;
      example: string;
    }>;
    frequencyDiscounts: Array<{
      frequency: string;
      discount: number;
      description: string;
    }>;
  };
  constructionPricing: {
    pricePerSqm: number;
    levels: Array<{
      name: string;
      description: string;
      multiplier: number;
    }>;
    examples: Array<{
      sqm: number;
      light: number;
      standard: number;
      heavy: number;
    }>;
  };
  windowPricing: {
    pricePerWindow: number;
    minimumWindows: number;
    minimumPrice: number;
    examples: Array<{
      windows: number;
      price: number;
    }>;
  };
  stairwellPricing: {
    minimumVisit: number;
    basePrice: number;
    perFloor: number;
    handrailsPerFloor: number;
    mailboxes: number;
    doors: number;
    basement: number;
  };
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function clampAtLeast(value: number | undefined, floor: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return floor;
  }
  return Math.max(value, floor);
}

export function applyPublicPricingFloors(config: PricingConfig): PricingConfig {
  const normalized = normalizePricingConfig(config);

  return {
    ...normalized,
    size_tiers: {
      ...normalized.size_tiers,
      tier_1: {
        ...normalized.size_tiers.tier_1,
        basePrice: clampAtLeast(
          normalized.size_tiers.tier_1?.basePrice,
          MARKET_FLOORS.residentialTiers.tier_1,
        ),
      },
      tier_2: {
        ...normalized.size_tiers.tier_2,
        basePrice: clampAtLeast(
          normalized.size_tiers.tier_2?.basePrice,
          MARKET_FLOORS.residentialTiers.tier_2,
        ),
      },
      tier_3: {
        ...normalized.size_tiers.tier_3,
        basePrice: clampAtLeast(
          normalized.size_tiers.tier_3?.basePrice,
          MARKET_FLOORS.residentialTiers.tier_3,
        ),
      },
      tier_4: {
        ...normalized.size_tiers.tier_4,
        basePrice: clampAtLeast(
          normalized.size_tiers.tier_4?.basePrice,
          MARKET_FLOORS.residentialTiers.tier_4,
        ),
      },
      tier_5: {
        ...normalized.size_tiers.tier_5,
        basePrice: clampAtLeast(
          normalized.size_tiers.tier_5?.basePrice,
          MARKET_FLOORS.residentialTiers.tier_5,
        ),
      },
      tier_6: {
        ...normalized.size_tiers.tier_6,
        basePrice: clampAtLeast(
          normalized.size_tiers.tier_6?.basePrice,
          MARKET_FLOORS.residentialTiers.tier_6,
        ),
      },
      tier_7: {
        ...normalized.size_tiers.tier_7,
        basePrice: clampAtLeast(
          normalized.size_tiers.tier_7?.basePrice,
          MARKET_FLOORS.residentialTiers.tier_7,
        ),
      },
      tier_8: {
        ...normalized.size_tiers.tier_8,
        basePrice: clampAtLeast(
          normalized.size_tiers.tier_8?.basePrice,
          MARKET_FLOORS.residentialTiers.tier_8,
        ),
      },
    },
    service_pricing: {
      ...normalized.service_pricing,
      office_price_per_sqm: clampAtLeast(
        replaceLegacyRate(
          normalized.service_pricing?.office_price_per_sqm,
          1.09,
          MARKET_FLOORS.officePricePerSqm,
        ),
        MARKET_FLOORS.officePricePerSqm,
      ),
      office_bathroom_price: clampAtLeast(
        normalized.service_pricing?.office_bathroom_price,
        MARKET_FLOORS.officeBathroomPrice,
      ),
      move_in_price_per_sqm: clampAtLeast(
        replaceLegacyRate(normalized.service_pricing?.move_in_price_per_sqm, 3.5, 3.2),
        MARKET_FLOORS.moveInPricePerSqm,
      ),
      move_out_price_per_sqm: clampAtLeast(
        replaceLegacyRate(normalized.service_pricing?.move_out_price_per_sqm, 3.2, 3.0),
        MARKET_FLOORS.moveOutPricePerSqm,
      ),
      move_both_price_per_sqm: clampAtLeast(
        replaceLegacyRate(normalized.service_pricing?.move_both_price_per_sqm, 3.0, 2.8),
        MARKET_FLOORS.moveBothPricePerSqm,
      ),
      construction_price_per_sqm: clampAtLeast(
        replaceLegacyRate(
          normalized.service_pricing?.construction_price_per_sqm,
          LEGACY_CONSTRUCTION_PRICE_PER_SQM,
          FALLBACK_SERVICE_PRICING.construction_price_per_sqm,
        ),
        MARKET_FLOORS.constructionPricePerSqm,
      ),
      construction_window_price: clampAtLeast(
        normalized.service_pricing?.construction_window_price,
        MARKET_FLOORS.constructionWindowPrice,
      ),
      construction_standard_mult: clampAtLeast(
        replaceLegacyRate(
          normalized.service_pricing?.construction_standard_mult,
          LEGACY_CONSTRUCTION_STANDARD_MULT,
          FALLBACK_SERVICE_PRICING.construction_standard_mult,
        ),
        FALLBACK_SERVICE_PRICING.construction_standard_mult,
      ),
      construction_heavy_mult: clampAtLeast(
        replaceLegacyRate(
          normalized.service_pricing?.construction_heavy_mult,
          LEGACY_CONSTRUCTION_HEAVY_MULT,
          FALLBACK_SERVICE_PRICING.construction_heavy_mult,
        ),
        FALLBACK_SERVICE_PRICING.construction_heavy_mult,
      ),
      window_price_per_window: clampAtLeast(
        normalized.service_pricing?.window_price_per_window,
        MARKET_FLOORS.windowPricePerWindow,
      ),
      hoarding_light_rate: clampAtLeast(
        normalized.service_pricing?.hoarding_light_rate,
        FALLBACK_SERVICE_PRICING.hoarding_light_rate,
      ),
      hoarding_standard_rate: clampAtLeast(
        normalized.service_pricing?.hoarding_standard_rate,
        FALLBACK_SERVICE_PRICING.hoarding_standard_rate,
      ),
      hoarding_heavy_rate: clampAtLeast(
        normalized.service_pricing?.hoarding_heavy_rate,
        FALLBACK_SERVICE_PRICING.hoarding_heavy_rate,
      ),
      hoarding_room_charge: clampAtLeast(
        normalized.service_pricing?.hoarding_room_charge,
        FALLBACK_SERVICE_PRICING.hoarding_room_charge,
      ),
      hoarding_bathroom_charge: clampAtLeast(
        normalized.service_pricing?.hoarding_bathroom_charge,
        FALLBACK_SERVICE_PRICING.hoarding_bathroom_charge,
      ),
      stairwell_base: clampAtLeast(
        normalized.service_pricing?.stairwell_base,
        MARKET_FLOORS.stairwellBase,
      ),
      stairwell_per_floor: clampAtLeast(
        normalized.service_pricing?.stairwell_per_floor,
        MARKET_FLOORS.stairwellPerFloor,
      ),
      stairwell_handrails_per_floor: clampAtLeast(
        normalized.service_pricing?.stairwell_handrails_per_floor,
        MARKET_FLOORS.stairwellHandrailsPerFloor,
      ),
      stairwell_mailboxes: clampAtLeast(
        normalized.service_pricing?.stairwell_mailboxes,
        MARKET_FLOORS.stairwellMailboxes,
      ),
      stairwell_doors: clampAtLeast(
        normalized.service_pricing?.stairwell_doors,
        MARKET_FLOORS.stairwellDoors,
      ),
      stairwell_basement: clampAtLeast(
        normalized.service_pricing?.stairwell_basement,
        MARKET_FLOORS.stairwellBasement,
      ),
    },
  };
}

export function buildPublicPricingSnapshot(
  inputConfig?: Partial<PricingConfig>,
): PublicPricingSnapshot {
  const config = applyPublicPricingFloors({
    ...FALLBACK_PRICING_CONFIG,
    ...inputConfig,
  });
  const servicePricing = config.service_pricing ?? FALLBACK_SERVICE_PRICING;

  const residentialTiers = RESIDENTIAL_TIER_INPUTS.map(tier => {
    const basePrice = config.size_tiers[tier.key]?.basePrice ?? 0;
    const regular = roundCurrency(
      basePrice +
        Math.max(tier.minRooms - 1, 0) * (config.room_charge ?? ROOM_CHARGE) +
        Math.max(tier.minBaths - 1, 0) * (config.bathroom_charge ?? BATHROOM_CHARGE),
    );
    const expert = roundCurrency(
      regular *
        (config.effort_multipliers.expert ?? FALLBACK_PRICING_CONFIG.effort_multipliers.expert),
    );

    return {
      name: tier.name,
      sqm: tier.sqm,
      regular,
      expert,
      rooms: tier.rooms,
      baths: tier.baths,
    };
  });

  const lowestBookablePrice = Math.min(...residentialTiers.map(tier => tier.regular));

  const officePricePerSqm = roundCurrency(servicePricing.office_price_per_sqm);
  const officeBathroomPrice = roundCurrency(servicePricing.office_bathroom_price);
  const moveInPricePerSqm = roundCurrency(servicePricing.move_in_price_per_sqm);
  const moveOutPricePerSqm = roundCurrency(servicePricing.move_out_price_per_sqm);
  const moveBothPricePerSqm = roundCurrency(servicePricing.move_both_price_per_sqm);
  const constructionPricePerSqm = roundCurrency(servicePricing.construction_price_per_sqm);
  const windowPricePerWindow = roundCurrency(servicePricing.window_price_per_window);
  const stairwellBase = roundCurrency(servicePricing.stairwell_base);
  const stairwellPerFloor = roundCurrency(servicePricing.stairwell_per_floor);

  return {
    lowestBookablePrice,
    addOnsPricing: { ...config.add_ons_pricing },
    residentialPricing: {
      tiers: residentialTiers,
      extras: [
        { name: 'Additional room', price: 5.5 },
        { name: 'Additional bathroom', price: 5 },
      ],
      frequencyDiscounts: [
        { frequency: 'One-time', discount: 0, description: 'Standard price' },
        { frequency: 'Monthly', discount: 5, description: '5% off' },
        { frequency: 'Bi-weekly', discount: 10, description: '10% off' },
        { frequency: 'Weekly', discount: 15, description: '15% off' },
      ],
    },
    moveInOutPricing: {
      services: [
        {
          name: 'Move-In Cleaning',
          pricePerSqm: moveInPricePerSqm,
          description: 'Prepare your new home',
        },
        {
          name: 'Move-Out Cleaning',
          pricePerSqm: moveOutPricePerSqm,
          description: 'Leave it spotless for the next tenant',
        },
        {
          name: 'Both (Move-In + Out)',
          pricePerSqm: moveBothPricePerSqm,
          discount: 10,
          description: 'Bundle both and save around 10%',
        },
      ],
      examples: [50, 80, 100, 120].map(sqm => ({
        sqm,
        moveIn: Math.round(sqm * moveInPricePerSqm * VAT_MULTIPLIER),
        moveOut: Math.round(sqm * moveOutPricePerSqm * VAT_MULTIPLIER),
        both: Math.round(sqm * moveBothPricePerSqm * VAT_MULTIPLIER),
      })),
    },
    officePricing: {
      pricePerSqm: officePricePerSqm,
      bathroomPrice: officeBathroomPrice,
      tiers: [
        {
          name: 'Small Office',
          sqm: '50-150m²',
          monthly: `from €${Math.round(50 * officePricePerSqm)}`,
          example: `100m² = €${Math.round(100 * officePricePerSqm)}/visit`,
        },
        {
          name: 'Medium Office',
          sqm: '150-300m²',
          monthly: `from €${Math.round(150 * officePricePerSqm)}`,
          example: `200m² = €${Math.round(200 * officePricePerSqm)}/visit`,
        },
        {
          name: 'Large Office',
          sqm: '300-500m²',
          monthly: `from €${Math.round(300 * officePricePerSqm)}`,
          example: `400m² = €${Math.round(400 * officePricePerSqm)}/visit`,
        },
        {
          name: 'Corporate',
          sqm: '500-2000m²',
          monthly: `from €${Math.round(500 * officePricePerSqm)}`,
          example: `1000m² = €${Math.round(1000 * officePricePerSqm)}/visit`,
        },
        {
          name: 'Enterprise',
          sqm: '2000m²+',
          monthly: 'Custom',
          example: 'Contact for quote',
        },
      ],
      frequencyDiscounts: [
        { frequency: 'One-time', discount: 0, description: 'Standard price' },
        { frequency: 'Monthly', discount: 5, description: 'Save 5%' },
        { frequency: 'Bi-weekly', discount: 10, description: 'Save 10%' },
        { frequency: 'Weekly', discount: 15, description: 'Save 15%' },
      ],
    },
    constructionPricing: {
      pricePerSqm: constructionPricePerSqm,
      levels: [
        {
          name: 'Light',
          description: 'Low difficulty. Basic dust & debris removal.',
          multiplier: 1.0,
        },
        {
          name: 'Standard',
          description: 'Medium difficulty. Full handover clean.',
          multiplier: servicePricing.construction_standard_mult,
        },
        {
          name: 'Heavy',
          description: 'High difficulty. Deep industrial clean.',
          multiplier: servicePricing.construction_heavy_mult,
        },
      ],
      examples: [50, 100, 200].map(sqm => ({
        sqm,
        light: Math.round(sqm * constructionPricePerSqm),
        standard: Math.round(
          sqm * constructionPricePerSqm * servicePricing.construction_standard_mult,
        ),
        heavy: Math.round(sqm * constructionPricePerSqm * servicePricing.construction_heavy_mult),
      })),
    },
    windowPricing: {
      pricePerWindow: windowPricePerWindow,
      minimumWindows: 5,
      minimumPrice: Math.max(25, Math.round(5 * windowPricePerWindow)),
      examples: [5, 10, 15, 20, 25, 30].map(windows => ({
        windows,
        price: Math.round(windows * windowPricePerWindow),
      })),
    },
    stairwellPricing: {
      minimumVisit: Math.max(28, Math.round(stairwellBase + stairwellPerFloor * 2)),
      basePrice: stairwellBase,
      perFloor: stairwellPerFloor,
      handrailsPerFloor: roundCurrency(servicePricing.stairwell_handrails_per_floor),
      mailboxes: roundCurrency(servicePricing.stairwell_mailboxes),
      doors: roundCurrency(servicePricing.stairwell_doors),
      basement: roundCurrency(servicePricing.stairwell_basement),
    },
  };
}

export const FALLBACK_PUBLIC_PRICING_SNAPSHOT = buildPublicPricingSnapshot();
